import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from 'react';

import PropTypes from 'prop-types';

import {
  getAssortmentTestConfigs,
  useAssortmentTestTracking,
} from '~/shared/utils/useAssortmentTestConfigs';
import { useLDFlags } from '~/shared/utils/useLDFlags';

// Module-level variable to persist disabled products across component remounts
// This clears on page reload, which is the desired behavior
const disabledProductIdsStore = new Set();

const AssortmentTestContext = createContext({
  activeAssortmentTests: [],
  excludeFplCodes: [],
  excludeTagIds: [],
  disabledBundleItems: new Set(),
  disabledProductIds: new Set(),
  registerDisabledBundleItem: () => {},
  unregisterDisabledBundleItem: () => {},
  registerDisabledProduct: () => {},
  unregisterDisabledProduct: () => {},
  isProductDisabled: () => false,
});

/**
 * Provider for assortment test configuration that works with both SSR and client-side rendering.
 *
 * @param {Object} props
 * @param {string[]} props.activeAssortmentTests - Array of active assortment test names from SSR
 * @param {string[]} [props.excludeFplCodes] - Optional pre-computed FPL codes to exclude (avoids recomputation)
 * @param {number[]} [props.excludeTagIds] - Optional pre-computed tag IDs to exclude (avoids recomputation)
 * @param {React.ReactNode} props.children
 */
export function AssortmentTestProvider({
  activeAssortmentTests,
  excludeFplCodes: precomputedFplCodes,
  excludeTagIds: precomputedTagIds,
  children,
}) {
  const ldFlags = useLDFlags();

  useAssortmentTestTracking(activeAssortmentTests);

  // Track which bundle items are disabled (by productIndex)
  const [disabledBundleItems, setDisabledBundleItems] = useState(new Set());

  // Track which products are disabled by assortment tests (by masterProductId)
  // Initialize from module-level store to persist across provider remounts
  const [disabledProductIds, setDisabledProductIds] = useState(
    () => new Set(disabledProductIdsStore)
  );

  // Register a bundle item as disabled
  const registerDisabledBundleItem = useCallback((productIndex) => {
    setDisabledBundleItems((prev) => {
      // Don't create a new Set if the index is already present
      if (prev.has(productIndex)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(productIndex);
      return next;
    });
  }, []);

  // Unregister a bundle item when it's no longer disabled or unmounts
  const unregisterDisabledBundleItem = useCallback((productIndex) => {
    setDisabledBundleItems((prev) => {
      const next = new Set(prev);
      next.delete(productIndex);
      return next;
    });
  }, []);

  // Register a product as disabled by masterProductId
  const registerDisabledProduct = useCallback((masterProductId) => {
    if (!disabledProductIdsStore.has(masterProductId)) {
      disabledProductIdsStore.add(masterProductId);
      setDisabledProductIds(new Set(disabledProductIdsStore));
    }
  }, []);

  // Unregister a product when it's no longer disabled
  const unregisterDisabledProduct = useCallback((masterProductId) => {
    if (disabledProductIdsStore.has(masterProductId)) {
      disabledProductIdsStore.delete(masterProductId);
      setDisabledProductIds(new Set(disabledProductIdsStore));
    }
  }, []);

  // Check if a product is disabled by masterProductId
  const isProductDisabled = useCallback(
    (masterProductId) => {
      return disabledProductIds.has(masterProductId);
    },
    [disabledProductIds]
  );

  const value = useMemo(() => {
    // Use pre-computed values if provided, otherwise compute them
    let excludeFplCodes = precomputedFplCodes;
    let excludeTagIds = precomputedTagIds;

    if (!excludeFplCodes || !excludeTagIds) {
      const computed = getAssortmentTestConfigs(activeAssortmentTests, ldFlags);
      excludeFplCodes = excludeFplCodes || computed.excludeFplCodes;
      excludeTagIds = excludeTagIds || computed.excludeTagIds;
    }

    return {
      activeAssortmentTests,
      excludeFplCodes,
      excludeTagIds,
      disabledBundleItems,
      disabledProductIds,
      registerDisabledBundleItem,
      unregisterDisabledBundleItem,
      registerDisabledProduct,
      unregisterDisabledProduct,
      isProductDisabled,
    };
  }, [
    activeAssortmentTests,
    precomputedFplCodes,
    precomputedTagIds,
    ldFlags,
    disabledBundleItems,
    disabledProductIds,
    registerDisabledBundleItem,
    unregisterDisabledBundleItem,
    registerDisabledProduct,
    unregisterDisabledProduct,
    isProductDisabled,
  ]);

  return (
    <AssortmentTestContext.Provider value={value}>
      {children}
    </AssortmentTestContext.Provider>
  );
}

AssortmentTestProvider.propTypes = {
  activeAssortmentTests: PropTypes.array,
  children: PropTypes.node,
  excludeFplCodes: PropTypes.array,
  excludeTagIds: PropTypes.array,
};

/**
 * Hook to access assortment test configuration from context.
 * Returns the active tests and computed exclusion lists.
 *
 * @returns {Object} { activeAssortmentTests, excludeFplCodes, excludeTagIds }
 */
export function useAssortmentTest() {
  const context = useContext(AssortmentTestContext);

  if (!context) {
    throw new Error(
      'useAssortmentTest must be used within AssortmentTestProvider'
    );
  }

  return context;
}
